import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ProductList from '../ProductList/ProductList'
import { urlHelpers } from '../../helpers'
import { DeseretBook } from '../../dbcom/api'

/**
 * Wrapper component for ProductList component to display recommended products
 * based on criteria specified in `data` prop. (Usually from Prismic).
 *
 * @returns {function} - React Component
 */
const DynamicProductList = ({ data }) => {
  const {
    recommendationType = '',
    count = 25,
    sku = '',
    taxonId = '',
    taxonIdChildren = '',
    indexType = 'user',
  } = data

  const [listData, setListData] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Fetches recommended products using either a sku or taxonID
   */
  const getProducts = async () => {
    try {
      const recommendationParams = {}

      // Add sku or TaxonId to recommendation search if present
      if (sku) {
        recommendationParams.sku = sku
      } else if (taxonId) {
        recommendationParams.taxonId = taxonId
      }

      // Fetch recommended products
      const productsResponse = await DeseretBook.recommendations().products({
        ...recommendationParams,
        options: { size: count },
      })

      if (
        productsResponse.status !== 'success' ||
        (productsResponse.data && !productsResponse.data.products)
      ) {
        return []
      }

      return productsResponse.data.products
    } catch (error) {
      return []
    }
  }

  /**
   * Builds a URL for prismic queries and product list data for product lists
   */
  const initialize = async () => {
    let newUrl = ''
    const user = window.__USER__

    // Determine API endpoint based on list type.
    if (recommendationType === 'previous-order' && user.previousOrder) {
      newUrl = `/api/recommendations/orders/${user.previousOrder}`
    } else if (recommendationType === 'cart-contents' && user.currentOrder) {
      newUrl = `/api/recommendations/orders/${user.currentOrder}`
    } else if (recommendationType === 'related-products' && sku) {
      newUrl = `/api/recommendations/products/${sku}`
    } else if (recommendationType === 'taxon-preview' && taxonId) {
      newUrl = `/api/prismic/product_lists/${taxonId}/taxon`
    } else if (recommendationType === 'author-taxon' && taxonId) {
      newUrl = `/products.json?sort[recent]=true&filters[producers][]=${taxonId}&${taxonIdChildren}&per_page=10`
    }

    newUrl = urlHelpers.addParams(newUrl, {
      index: indexType,
      size: count,
    })

    setListData({
      ...data,
      products: await getProducts(),
      productURL: newUrl,
    })
    setIsLoading(false)
  }

  useEffect(() => {
    initialize()
  }, [])

  return !isLoading && <ProductList data={listData} isDynamic />
}

DynamicProductList.defaultProps = {
  data: {},
}

DynamicProductList.propTypes = {
  data: PropTypes.shape({
    recommendationType: PropTypes.string,
    count: PropTypes.number,
    sku: PropTypes.string,
    taxonId: PropTypes.number,
    taxonIdChildren: PropTypes.arrayOf(PropTypes.any),
    indexType: PropTypes.string,
  }),
}

export default DynamicProductList
